import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  IndianRupee,
  Percent,
  Clock,
  BarChart2,
  TrendingUp,
  GitCompare,
  Flame,
  ArrowRightLeft,
  Layers,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import Swal from "sweetalert2";
import "./ComparisonCalculator.css";

const CALCS = [
  {
    value: "mfvsfd",
    label: "MF vs FD",
    fullLabel: "Mutual Fund vs Fixed Deposit",
    desc: "Compare lumpsum growth side by side",
    color: "#2563eb",
    colorEnd: "#60a5fa",
    Icon: GitCompare,
  },
  {
    value: "inflation",
    label: "Inflation Impact",
    fullLabel: "Inflation-Adjusted Return",
    desc: "See your real purchasing power",
    color: "#ef4444",
    colorEnd: "#fca5a5",
    Icon: Flame,
  },
  {
    value: "rdvsip",
    label: "RD vs SIP",
    fullLabel: "Recurring Deposit vs SIP",
    desc: "Monthly savings — which wins?",
    color: "#0891b2",
    colorEnd: "#38bdf8",
    Icon: ArrowRightLeft,
  },
  {
    value: "fdladdersip",
    label: "FD Ladder vs SIP",
    fullLabel: "FD Ladder vs SIP Planning",
    desc: "Staggered FD against long SIP",
    color: "#10b981",
    colorEnd: "#6ee7b7",
    Icon: Layers,
  },
];

const formatINR = (n) => {
  if (isNaN(n)) return "-";
  return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const validate = (...vals) =>
  vals.every((v) => v !== "" && !isNaN(+v) && +v >= 0);

export default function ComparisonCalculator() {
  const [calcType, setCalcType] = useState("mfvsfd");
  const [amount, setAmount] = useState("");
  const [rateMF, setRateMF] = useState("");
  const [rateFD, setRateFD] = useState("");
  const [years, setYears] = useState("");
  const [inflation, setInflation] = useState("");
  const [sipAmount, setSipAmount] = useState("");
  const [rdAmount, setRdAmount] = useState("");
  const [ladderYears, setLadderYears] = useState("");
  const [result, setResult] = useState(null);

  const active = CALCS.find((c) => c.value === calcType);
  const accentColor = active?.color || "#2563eb";
  const accentEnd = active?.colorEnd || "#60a5fa";

  /* ── Calculations with Yearly Breakdown ── */
  const calcMFvsFD = () => {
    if (!validate(amount, rateMF, rateFD, years)) return null;
    const P = +amount,
      rMF = +rateMF / 100,
      rFD = +rateFD / 100,
      n = +years;

    const breakdown = [];
    for (let y = 1; y <= n; y++) {
      const valMF = P * Math.pow(1 + rMF, y);
      const valFD = P * Math.pow(1 + rFD, y);
      breakdown.push({
        year: y,
        leftVal: Math.round(valMF),
        rightVal: Math.round(valFD),
        diff: Math.round(valMF - valFD), // MF - FD
      });
    }

    const fvMF = P * Math.pow(1 + rMF, n);
    const fvFD = P * Math.pow(1 + rFD, n);

    return {
      left: {
        label: "Mutual Fund",
        fv: fvMF,
        invested: P,
        growth: fvMF - P,
        color: "#2563eb",
      },
      right: {
        label: "Fixed Deposit",
        fv: fvFD,
        invested: P,
        growth: fvFD - P,
        color: "#f59e0b",
      },
      breakdown,
    };
  };

  const calcInflation = () => {
    if (!validate(amount, rateMF, inflation, years)) return null;
    const P = +amount,
      r = +rateMF / 100,
      n = +years,
      i = +inflation / 100;

    const breakdown = [];
    for (let y = 1; y <= n; y++) {
      const nom = P * Math.pow(1 + r, y);
      const adj = nom / Math.pow(1 + i, y);
      breakdown.push({
        year: y,
        leftVal: Math.round(nom),
        rightVal: Math.round(adj),
        diff: Math.round(nom - adj), // Purchasing power lost
      });
    }

    const fvNom = P * Math.pow(1 + r, n);
    const fvAdj = fvNom / Math.pow(1 + i, n);

    return {
      left: {
        label: "Nominal Return",
        fv: fvNom,
        invested: P,
        growth: fvNom - P,
        color: "#2563eb",
      },
      right: {
        label: "Inflation-Adjusted Return",
        fv: fvAdj,
        invested: P,
        growth: fvAdj - P,
        color: "#ef4444",
      },
      breakdown,
    };
  };

  const calcRDvsSIP = () => {
    if (!validate(rdAmount, sipAmount, rateMF, years)) return null;
    const A = +rdAmount,
      SIP = +sipAmount,
      r = +rateMF / 100,
      t = +years;

    const rq = r / 4;
    const rm = r / 12;

    const breakdown = [];
    for (let y = 1; y <= t; y++) {
      const m = y * 12;
      const valRD =
        A * ((Math.pow(1 + rq, 4 * y) - 1) / (1 - Math.pow(1 + rq, -1 / 3)));
      const valSIP = SIP * ((Math.pow(1 + rm, m) - 1) / rm);
      breakdown.push({
        year: y,
        leftVal: Math.round(valRD),
        rightVal: Math.round(valSIP),
        diff: Math.round(valSIP - valRD), // SIP - RD
      });
    }

    const months = t * 12;
    const fvRD =
      A * ((Math.pow(1 + rq, 4 * t) - 1) / (1 - Math.pow(1 + rq, -1 / 3)));
    const fvSIP = SIP * ((Math.pow(1 + rm, months) - 1) / rm);

    return {
      left: {
        label: "Recurring Deposit",
        fv: fvRD,
        invested: A * months,
        growth: fvRD - A * months,
        color: "#f59e0b",
      },
      right: {
        label: "SIP Investment",
        fv: fvSIP,
        invested: SIP * months,
        growth: fvSIP - SIP * months,
        color: "#2563eb",
      },
      breakdown,
    };
  };

  const calcFDLadderVsSIP = () => {
    if (!validate(amount, rateFD, ladderYears)) return null;
    const P = +amount,
      r = +rateFD / 100,
      n = +ladderYears;
    const split = P / 3;
    const rm = r / 12;

    const breakdown = [];
    for (let y = 1; y <= n; y++) {
      const fdTotal =
        split * Math.pow(1 + r, Math.min(y, 1)) +
        split * Math.pow(1 + r, Math.min(y, 2)) +
        split * Math.pow(1 + r, Math.min(y, 3));
      const sipTotal = split * ((Math.pow(1 + rm, y * 12) - 1) / rm);
      breakdown.push({
        year: y,
        leftVal: Math.round(fdTotal),
        rightVal: Math.round(sipTotal),
        diff: Math.round(sipTotal - fdTotal), // SIP - FD
      });
    }

    const totalFD =
      split * Math.pow(1 + r, 1) +
      split * Math.pow(1 + r, 2) +
      split * Math.pow(1 + r, 3);
    const fvSIP = split * ((Math.pow(1 + rm, n * 12) - 1) / rm);

    return {
      left: {
        label: "FD Ladder",
        fv: totalFD,
        invested: P,
        growth: totalFD - P,
        color: "#f59e0b",
      },
      right: {
        label: "SIP Plan",
        fv: fvSIP,
        invested: split * n * 12,
        growth: fvSIP - split * n * 12,
        color: "#10b981",
      },
      breakdown,
    };
  };

  const handleCalculate = () => {
    let output = null;
    if (calcType === "mfvsfd") output = calcMFvsFD();
    if (calcType === "inflation") output = calcInflation();
    if (calcType === "rdvsip") output = calcRDvsSIP();
    if (calcType === "fdladdersip") output = calcFDLadderVsSIP();

    if (!output) {
      Swal.fire({
        title: "Missing Details",
        text: "Please fill in all required fields with valid values.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: accentColor,
        width: 380,
      });
      return;
    }
    setResult(output);
  };

  const handleReset = () => {
    setAmount("");
    setRateMF("");
    setRateFD("");
    setYears("");
    setInflation("");
    setSipAmount("");
    setRdAmount("");
    setLadderYears("");
    setResult(null);
  };

  const isMFvsFD = calcType === "mfvsfd";
  const isInflation = calcType === "inflation";
  const isRDvsSIP = calcType === "rdvsip";
  const isFDLadder = calcType === "fdladdersip";

  return (
    <div className="cc-wrapper">
      <Navbar />

      {/* ── Hero ── */}
      <section className="cc-hero">
        <div className="cc-hero-bg" />
        <div className="cc-hero-dots" />
        <div className="cc-hero-glow cc-glow-l" />
        <div className="cc-hero-glow cc-glow-r" />

        <div className="cc-hero-inner">
          <Link to="/calculators" className="cc-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="cc-hero-top">
            <div>
              <div className="cc-eyebrow">
                <BarChart2 size={12} /> Comparison &amp; Inflation
              </div>
              <h1 className="cc-hero-title">
                Compare &amp; Beat
                <br />
                <span
                  className="cc-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  Every Investment Option
                </span>
              </h1>
              <p className="cc-hero-sub">
                MF vs FD, RD vs SIP, inflation impact, FD ladder — see exactly
                which option puts more money in your pocket.
              </p>
            </div>

            <div
              className="cc-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {active ? (
                <active.Icon size={36} color="#fff" />
              ) : (
                <BarChart2 size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Calculator chips */}
          <div className="cc-type-chips">
            {CALCS.map(({ value, label, desc, color, Icon }) => (
              <button
                key={value}
                className={`cc-chip${calcType === value ? " cc-chip--active" : ""}`}
                style={
                  calcType === value
                    ? { borderColor: color, background: `${color}14`, color }
                    : {}
                }
                onClick={() => {
                  setCalcType(value);
                  setResult(null);
                }}
              >
                <Icon size={13} />
                <span>{label}</span>
                <span className="cc-chip-sub">{desc}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main ── */}
      <div className="cc-content">
        {/* Input Card */}
        <div className="cc-card">
          <div
            className="cc-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
            }}
          />
          <div className="cc-card-body">
            {/* Active pill */}
            <div
              className="cc-active-pill"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}22`,
                color: accentColor,
              }}
            >
              {active && <active.Icon size={13} />}
              {active?.fullLabel} — {active?.desc}
            </div>

            <div className="cc-section-title">
              Input Details
              <span className="cc-section-line" />
            </div>

            <div className="cc-grid-2">
              {/* MF vs FD */}
              {isMFvsFD && (
                <>
                  <div className="cc-field">
                    <label className="cc-label">
                      <IndianRupee size={12} /> Investment Amount (₹)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 100000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Percent size={12} /> MF Expected Return (%)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 12"
                        value={rateMF}
                        onChange={(e) => setRateMF(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Percent size={12} /> FD Interest Rate (%)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 7"
                        value={rateFD}
                        onChange={(e) => setRateFD(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Clock size={12} /> Duration (Years)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 10"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Inflation */}
              {isInflation && (
                <>
                  <div className="cc-field">
                    <label className="cc-label">
                      <IndianRupee size={12} /> Investment Amount (₹)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 100000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Percent size={12} /> Expected Return (%)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 12"
                        value={rateMF}
                        onChange={(e) => setRateMF(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Flame size={12} /> Inflation Rate (%)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 6"
                        value={inflation}
                        onChange={(e) => setInflation(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Clock size={12} /> Duration (Years)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 10"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* RD vs SIP */}
              {isRDvsSIP && (
                <>
                  <div className="cc-field">
                    <label className="cc-label">
                      <IndianRupee size={12} /> RD Monthly Amount (₹)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 5000"
                        value={rdAmount}
                        onChange={(e) => setRdAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <IndianRupee size={12} /> SIP Monthly Amount (₹)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 5000"
                        value={sipAmount}
                        onChange={(e) => setSipAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Percent size={12} /> Expected Return (%)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 7"
                        value={rateMF}
                        onChange={(e) => setRateMF(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Clock size={12} /> Duration (Years)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 5"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* FD Ladder vs SIP */}
              {isFDLadder && (
                <>
                  <div className="cc-field">
                    <label className="cc-label">
                      <IndianRupee size={12} /> Total FD Amount (₹)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 300000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Percent size={12} /> FD Interest Rate (%)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 7"
                        value={rateFD}
                        onChange={(e) => setRateFD(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-field">
                    <label className="cc-label">
                      <Clock size={12} /> SIP Duration (Years)
                    </label>
                    <div className="cc-input-wrap">
                      <span className="cc-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="cc-input"
                        placeholder="e.g., 3"
                        value={ladderYears}
                        onChange={(e) => setLadderYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="cc-btn-row">
              <button
                className="cc-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
                onClick={handleCalculate}
              >
                <Calculator size={16} /> Calculate
              </button>
              <button className="cc-reset-btn" onClick={handleReset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {result && (
          <>
            <div className="cc-card">
              <div
                className="cc-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="cc-card-body">
                <div className="cc-section-title">
                  Comparison Result
                  <span className="cc-section-line" />
                </div>

                {/* Two-column comparison */}
                <div className="cc-compare-grid">
                  {[result.left, result.right].map((col) => (
                    <div key={col.label} className="cc-compare-col">
                      <div
                        className="cc-compare-badge"
                        style={{
                          background: `${col.color}14`,
                          color: col.color,
                          borderColor: `${col.color}22`,
                        }}
                      >
                        <TrendingUp size={13} /> {col.label}
                      </div>
                      <div
                        className="cc-compare-value"
                        style={{ color: col.color }}
                      >
                        ₹ {formatINR(Math.round(col.fv))}
                      </div>
                      <div className="cc-compare-sub">Future Value</div>
                      <div className="cc-compare-rows">
                        <div className="cc-compare-row">
                          <span>Invested</span>
                          <strong>
                            ₹ {formatINR(Math.round(col.invested))}
                          </strong>
                        </div>
                        <div className="cc-compare-row">
                          <span>Growth</span>
                          <strong
                            style={{
                              color: col.growth >= 0 ? "#10b981" : "#ef4444",
                            }}
                          >
                            {col.growth >= 0 ? "+" : "-"}₹{" "}
                            {formatINR(Math.abs(Math.round(col.growth)))}
                          </strong>
                        </div>
                        <div className="cc-compare-row">
                          <span>Return %</span>
                          <strong style={{ color: col.color }}>
                            {col.invested > 0
                              ? ((col.growth / col.invested) * 100).toFixed(1)
                              : "0.0"}
                            %
                          </strong>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* VS divider */}
                  <div className="cc-compare-vs">VS</div>
                </div>

                {/* Winner banner */}
                {(() => {
                  const leftWins = result.left.fv >= result.right.fv;
                  const winner = leftWins ? result.left : result.right;
                  const diff = Math.abs(result.left.fv - result.right.fv);
                  return (
                    <div
                      className="cc-winner-banner"
                      style={{
                        background: `${winner.color}0d`,
                        borderColor: `${winner.color}22`,
                      }}
                    >
                      <GitCompare size={16} style={{ color: winner.color }} />
                      <span>
                        <strong style={{ color: winner.color }}>
                          {winner.label}
                        </strong>{" "}
                        outperforms by{" "}
                        <strong style={{ color: winner.color }}>
                          ₹ {formatINR(Math.round(diff))}
                        </strong>
                      </span>
                    </div>
                  );
                })()}

                {/* Breakdown bars */}
                <div className="cc-section-title cc-section-title--spaced">
                  Future Value Comparison
                  <span className="cc-section-line" />
                </div>
                {(() => {
                  const maxFV = Math.max(result.left.fv, result.right.fv);
                  return [result.left, result.right].map((col) => (
                    <div key={col.label} className="cc-bar-group">
                      <div className="cc-bar-row">
                        <span className="cc-bar-label">{col.label}</span>
                        <span className="cc-bar-amt">
                          ₹ {formatINR(Math.round(col.fv))}
                        </span>
                      </div>
                      <div className="cc-bar-track">
                        <div
                          className="cc-bar-fill"
                          style={{
                            width: `${Math.round((col.fv / maxFV) * 100)}%`,
                            background: `linear-gradient(90deg, ${col.color}, ${col.color}88)`,
                          }}
                        />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* ── Tabular Breakdown ── */}
            <div className="cc-card">
              <div
                className="cc-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="cc-card-body">
                <div className="cc-section-title">
                  Yearly Schedule
                  <span className="cc-section-line" />
                </div>

                <div className="cc-table-wrap">
                  <table className="cc-table">
                    <thead>
                      <tr>
                        <th className="cc-th">Year</th>
                        <th className="cc-th">{result.left.label} (₹)</th>
                        <th className="cc-th">{result.right.label} (₹)</th>
                        <th className="cc-th">Difference (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.breakdown.map((row, i) => (
                        <tr
                          key={row.year}
                          className={i % 2 === 0 ? "cc-tr-even" : "cc-tr-odd"}
                        >
                          <td className="cc-td cc-td-year">Year {row.year}</td>
                          <td className="cc-td cc-td-mono">
                            {formatINR(row.leftVal)}
                          </td>
                          <td className="cc-td cc-td-mono">
                            {formatINR(row.rightVal)}
                          </td>
                          <td
                            className={`cc-td cc-td-mono cc-td-bold ${
                              row.diff >= 0
                                ? "cc-text-success"
                                : "cc-text-danger"
                            }`}
                          >
                            {row.diff > 0 ? "+" : ""}
                            {formatINR(row.diff)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CTA Banner */}
        <div className="cc-cta-banner">
          <div className="cc-cta-text">
            <h3 className="cc-cta-title">
              Not sure which option fits your goals?
            </h3>
            <p className="cc-cta-sub">
              Our advisors will match the right instrument — MF, FD, SIP or a
              blend — to your exact financial situation.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_COMPARE)} className="cc-cta-btn">
            Book Free Consultation →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
